// Browser loader for the ozcrypto WASM core.
//
// The Emscripten module (ozcrypto.js glue + ozcrypto.wasm) is served as a static
// asset. Because the ENTIRE E2E guarantee collapses if a hostile server ships a
// backdoored core (fixed nonce, key exfiltration, a self-test that always passes),
// both files are integrity-pinned and E2E FAILS CLOSED on mismatch:
//   • the glue <script> carries an SRI hash (browser refuses a tampered script);
//   • the .wasm — where the AES actually lives, and which a <script> SRI does NOT
//     cover — is fetched and SHA-384-checked before it is ever instantiated.
//
// Regenerate the pins after rebuilding ozcrypto (values are for the SERVED files,
// which the Dockerfile copies from top-level ozcrypto/):
//   printf 'sha384-%s\n' "$(openssl dgst -sha384 -binary ozcrypto/ozcrypto.js  | openssl base64 -A)"
//   printf 'sha384-%s\n' "$(openssl dgst -sha384 -binary ozcrypto/ozcrypto.wasm | openssl base64 -A)"
// Served at <static>/e2e/ozcrypto.js and .wasm (see docker/nexus/Dockerfile).

const BASE = 'e2e/';

// SHA-384 integrity pins for the served core (ozcrypto build 2026-07-15).
export const OZ_JS_SRI = 'sha384-Wyn8Q+wjClOX56Rkv7h2va1FkrRcWSa7I8jn7rvqLlFH0fUPyA11gOrckrp6TV3S';
export const OZ_WASM_SRI = 'sha384-DOK3qVQVRJUA5Kko8vVBqHhhHHWsF4fBhZtWyYqe2im2tSXLTMTMBLOXoCvIWCik';

let scriptPromise = null;

function injectScript() {
  if (typeof window !== 'undefined' && window.OzCrypto) return Promise.resolve(window.OzCrypto);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = BASE + 'ozcrypto.js';
    s.async = true;
    // SRI: the browser will refuse to execute a tampered glue script. crossOrigin
    // is required for the integrity check to be enforced.
    s.integrity = OZ_JS_SRI;
    s.crossOrigin = 'anonymous';
    s.onload = () => resolve(window.OzCrypto);
    s.onerror = () => reject(new Error('E2E: failed to load or verify ozcrypto.js'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// Fetch the .wasm and verify its SHA-384 BEFORE instantiation; throw (fail closed)
// on mismatch so a swapped core can never run.
async function verifiedWasmBinary() {
  const resp = await fetch(BASE + 'ozcrypto.wasm', { cache: 'force-cache' });
  if (!resp.ok) throw new Error('E2E: failed to fetch ozcrypto.wasm');
  const buf = await resp.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-384', buf);
  const b64 = btoa(String.fromCharCode.apply(null, new Uint8Array(digest)));
  if ('sha384-' + b64 !== OZ_WASM_SRI) {
    throw new Error('E2E: ozcrypto.wasm integrity check FAILED — refusing to run');
  }
  return new Uint8Array(buf);
}

// Returns a factory `() => Promise<Module>` for aead.loadAead(). The verified wasm
// bytes are passed as `wasmBinary`, so Emscripten instantiates OUR checked buffer
// instead of fetching the .wasm itself.
export async function ozcryptoFactory() {
  const OzCrypto = await injectScript();
  const wasmBinary = await verifiedWasmBinary();
  return () => OzCrypto({ wasmBinary, locateFile: (path) => BASE + path });
}
